/**
 * 需要想定 daily の「差」を定義する唯一の module。
 *
 * 「保存済み baseline と編集中 current の差」を、単一コマの比較 primitive
 * `diffSlot` に一元化し、そこから 2 つの派生を construction で導出する:
 *
 *   - dirty 判定 (`computeForecastDiff`): 差があるコマ集合 (dirtyKomas) と
 *     画面全体の isDirty (設計書 §5 主要意思決定「dirty 判定」)。
 *   - 表示差 (`computeSaveDiff`): KomaGrid「差」列 (spec AC-DFD-001「差
 *     （保存済みとの差分）」) の符号付き表示文字列。
 *
 * 両者が `diffSlot` を共有するため、「表示差が非空のコマは必ず dirty」という
 * 関係はコメント上の invariant ではなく型・構造で保証される (表示非空 ⟹
 * delta が非 0 の数値 ⟹ `diffSlot` の changed=true ⟹ dirtyKomas に含まれる)。
 * これにより「コマの差とは何か」を答える箇所が本 module 1 つに閉じる。
 *
 * dirty 判定は値比較 (空セル null ≠ 0 ≠ 値) に加え、取込元メタ (provider /
 * providerLabel / providerSlots) の差分と manualEditKomas (手修正コマ) の対称差も
 * 含める。manualEditKomas は Save RPC で永続化され確定レコードへも引き継がれる
 * 業務シグナルであり、含めないと「手修正コマを取込元と同値に戻して保存 →
 * リセットで manualEditKomas が空になる」ケースで値比較のみでは clean 判定され、
 * マーカー消去を保存で永続化できなくなる (review 指摘 PR #3199)。
 *
 * 表示の「差」列は `computeSlotDiff` (`slotHelpers.ts`、実績との差分用) とは
 * 対比先が異なる別概念 (実績 vs 需要想定 ではなく、保存済みの需要想定値 vs
 * 編集中の需要想定値)。役割分離のため本 module に置く (grill Q9)。
 */
import { formatSlotValue } from '~/pages/DemandForecastDaily/models/slotHelpers'

import type { KomaSlots, RetailerFieldsSnapshot } from './types'

/** `computeForecastDiff` の結果。 */
export type ForecastDiffResult = {
  /** baseline と値が異なるコマ番号 (1-indexed) の集合。 */
  dirtyKomas: Set<number>
  /** dirtyKomas が非空、または取込元メタが異なる場合に true。 */
  isDirty: boolean
}

/** 差列 (「保存済みとの差分」) の表示結果 (spec AC-DFD-001)。 */
export type SaveDiffResult = {
  /** 生差分 kWh (current - baseline)。「両方数値」時のみ number、それ以外 undefined */
  delta: number | undefined
  /** 表示文字列。両方数値 && delta !== 0 のみ "+N" or "−N"、それ以外は "" (空表示) */
  display: string
  /** display の符号 (a11y ラベル生成用)。delta > 0 → '+' / delta < 0 → '-' / それ以外 null */
  sign: '+' | '-' | null
}

/**
 * 単一コマの baseline↔current 比較結果 (差の最小単位)。dirty 判定と表示差の
 * 双方がここから導出される (slotHelpers.ts の `SlotDiff` は実績差分用の別概念)。
 */
type KomaSlotDiff = {
  /** slot 値が baseline と異なるか (= コマ単位 dirty の値由来分)。 */
  changed: boolean
  /**
   * 表示可能な生差分 (current - baseline)。両方が数値のときのみ number
   * (差 0 なら 0)、片方でも空セル (undefined) なら undefined。
   */
  delta: number | undefined
}

/**
 * 1 コマ分の baseline / current を比較する差の primitive。
 *
 * - `changed`: 空セル null ≠ 0 ≠ 値 で厳密比較 (undefined→値 / 値→undefined /
 *   値→別の値 はいずれも changed=true、両方 undefined / 同値は changed=false)。
 * - `delta`: 両方が数値のときのみ生差分を持つ (表示に使える分)。片方でも空なら
 *   undefined。changed だが delta=undefined = 「dirty だが表示は空」の状態。
 */
function diffSlot(
  baseline: number | undefined,
  current: number | undefined,
): KomaSlotDiff {
  const bothNumeric = baseline !== undefined && current !== undefined
  return {
    changed: baseline !== current,
    delta: bothNumeric ? current - baseline : undefined,
  }
}

/** 48 コマの値配列を index ごとに比較する (undefined ≠ 0 ≠ 値)。 */
function slotsEqual(a: KomaSlots, b: KomaSlots): boolean {
  if (a.length !== b.length) return false
  return a.every((v, i) => v === b[i])
}

/**
 * baseline と current の 6 field snapshot を比較し、コマ単位の dirty 集合と
 * 画面全体の isDirty を導出する。
 */
export function computeForecastDiff(
  baseline: RetailerFieldsSnapshot,
  current: RetailerFieldsSnapshot,
): ForecastDiffResult {
  const dirtyKomas = new Set<number>()
  const length = Math.max(
    baseline.forecastSlots.length,
    current.forecastSlots.length,
  )
  for (let i = 0; i < length; i++) {
    if (diffSlot(baseline.forecastSlots[i], current.forecastSlots[i]).changed) {
      dirtyKomas.add(i + 1)
    }
  }

  // manualEditKomas の対称差 (マーカーの付け外しがあったコマ) も dirtyKomas に
  // 加える (isDirty は dirtyKomas.size > 0 経由で自動的に反映される)。
  for (const koma of baseline.manualEditKomas) {
    if (!current.manualEditKomas.has(koma)) dirtyKomas.add(koma)
  }
  for (const koma of current.manualEditKomas) {
    if (!baseline.manualEditKomas.has(koma)) dirtyKomas.add(koma)
  }

  const metaDiffers =
    baseline.provider !== current.provider ||
    baseline.providerOther !== current.providerOther ||
    baseline.providerLabel !== current.providerLabel ||
    !slotsEqual(baseline.providerSlots, current.providerSlots)

  return {
    dirtyKomas,
    isDirty: dirtyKomas.size > 0 || metaDiffers,
  }
}

/**
 * 1 コマの保存済み値 (baseline) と編集中の値 (current) の差を、spec
 * AC-DFD-001「差 (保存済みとの差分)」・AC-DFD-004「空セル ≠ 0」に従って
 * 符号付き表示へ導出する (grill Q2/Q6/Q5、AC-03 4分岐)。
 *
 * `diffSlot` を土台にするため dirty 判定 (`computeForecastDiff`) と起点が構造的に
 * 揃う: delta が非 0 の数値になるコマは `diffSlot` の changed=true であり、
 * `computeForecastDiff` の dirtyKomas に必ず含まれる (表示が非空になるのは、
 * そのうち整数表示で 0 にならないコマだけ)。
 *
 * 表示の 4 分岐 (Q6-A):
 *   (a) 両方 undefined                  → delta undefined、空表示
 *   (b) 片方だけ undefined              → delta undefined、空表示
 *   (c) 両方数値 && 差 = 0              → delta 0、空表示 (AC-DFD-004: 空セルと
 *       0 の視覚区別のため差 0 も空表示にして「未変更」を明示する)
 *       表示桁 (整数) に丸めて 0 になる差 (|delta| < 0.5) も空表示にする
 *   (d) 両方数値 && 表示上も差 !== 0     → delta、"+N" or "−N"
 *
 * 負符号は半角ハイフン (U+002D) ではなく減算記号 U+2212 ("−") を使う
 * (design forecast-today.html L246 / test-design TC-DFD-D-54.1 準拠)。
 */
export function computeSaveDiff(
  baseline: number | undefined,
  current: number | undefined,
): SaveDiffResult {
  const { delta } = diffSlot(baseline, current)
  // 分岐 (a)+(b): 片方でも空 (delta undefined) / 分岐 (c): 両方数値で差 0。
  if (delta === undefined || delta === 0) {
    return { delta, display: '', sign: null }
  }
  // 表示桁 (整数) に丸めて 0 になる差 (|delta| < 0.5) も、差 0 と同じく空表示にする
  // (AC-DFC-EDIT-003: 小数の保持値は表示だけ整数。"+0" を出さない)。
  if (formatSlotValue(Math.abs(delta)) === '0') {
    return { delta, display: '', sign: null }
  }
  // 分岐 (d): 両方数値 && 差 !== 0。減算記号は U+2212 ("−")、加算記号は "+"。
  const sign: '+' | '-' = delta > 0 ? '+' : '-'
  const displaySign = sign === '+' ? '+' : '−'
  return {
    delta,
    display: `${displaySign}${formatSlotValue(Math.abs(delta))}`,
    sign,
  }
}
